/**
 * DashboardGrid Component
 *
 * Responsive grid layout for dashboard content with predefined breakpoints.
 * Provides consistent spacing and responsive behavior across all dashboards.
 *
 * Usage:
 * <DashboardGrid cols={3}>
 *   <StatCard ... />
 *   <StatCard ... />
 *   <StatCard ... />
 * </DashboardGrid>
 */

import type { ReactNode } from 'react';

export interface DashboardGridProps {
  /** Number of columns on desktop (default: 3) */
  cols?: 1 | 2 | 3 | 4;

  /** Gap size between grid items */
  gap?: 'sm' | 'md' | 'lg';

  /** Custom className */
  className?: string;

  /** Grid children */
  children: ReactNode;
}

export function DashboardGrid({
  cols = 3,
  gap = 'md',
  className = '',
  children,
}: DashboardGridProps) {
  const colsMap = {
    1: 'lg:grid-cols-1',
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
  };

  const gapMap = {
    sm: 'gap-3',
    md: 'gap-6',
    lg: 'gap-8',
  };

  return (
    <div
      className={`
        grid grid-cols-1 md:grid-cols-2 ${colsMap[cols]}
        ${gapMap[gap]}
        ${className}
      `}
    >
      {children}
    </div>
  );
}

export default DashboardGrid;
